import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Save, AlertCircle, Info, Loader2, RotateCcw } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import RoleGuard from "@/components/auth/RoleGuard";
import { PageLoader } from "@/components/loading/PageLoader";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { PageActionBar } from "@/components/layout/PageActionBar";
import { useInventory } from "@/features/inventory/hooks/useInventory";
import { useSkuRules } from "@/features/sku-rules/hooks/useSkuRules";
import { SkuRulesEditor } from "@/features/sku-rules/components/SkuRulesEditor";
import { MissingGarmentsAlert } from "@/features/sku-rules/components/MissingGarmentsAlert";
import type { SKURulesState, NewGarmentInputs, SKURuleCategoryKey } from "@/features/sku-rules/types";

interface MissingSkuEntry {
  sku: string;
  base: string;
  title?: string;
  stockLevel?: number;
  imageUrl?: string;
}

const SKURules: React.FC = () => {
  const { t } = useTranslation('inventory');
  const { products } = useInventory();
  const { rules: loadedRules, loading: rulesLoading, saveRules, isSaving } = useSkuRules();

  usePageTitle(t('rules.title'), t('rules.subtitle'));

  const [rules, setRules] = useState<SKURulesState>(loadedRules);
  const [originalRules, setOriginalRules] = useState<SKURulesState>(loadedRules);
  const [newGarmentInputs, setNewGarmentInputs] = useState<NewGarmentInputs>({
    doubleSidedPrefixes: '',
    standardGarments: '',
    firstTwoSegments: '',
    wrapperTypes: '',
    passThrough: ''
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [missingSkus, setMissingSkus] = useState<MissingSkuEntry[]>([]);

  const inventorySkus = useMemo(
    () =>
      products
        .filter((product: { productionType?: string }) => product.productionType !== 'PAP')
        .map((product: { sku?: string }) => product.sku?.trim().toUpperCase())
        .filter((sku: string | undefined): sku is string => Boolean(sku)),
    [products]
  );

  const appendRuleValue = (category: SKURuleCategoryKey, value: string) => {
    const cleanedValue = value.trim().toUpperCase();
    if (!cleanedValue) return;

    setRules((prev) => {
      const existing = new Set(prev[category] || []);
      if (existing.has(cleanedValue)) {
        return prev;
      }

      return {
        ...prev,
        [category]: [...(prev[category] || []), cleanedValue].sort(),
      };
    });
  };

  // Sync loaded rules to local state
  useEffect(() => {
    if (!rulesLoading) {
      setRules(loadedRules);
      setOriginalRules(loadedRules);
    }
  }, [loadedRules, rulesLoading]);

  // Track changes against the original loaded rules
  useEffect(() => {
    const hasChanges = JSON.stringify(rules) !== JSON.stringify(originalRules);
    setHasChanges(hasChanges);
  }, [rules, originalRules]);

  const isSkuCoveredByRules = (sku: string, currentRules: SKURulesState): boolean => {
    const parts = sku.split('-');
    if (parts.length === 0) return false;

    const base = parts[0];
    if (!base) return false;

    if (currentRules.passThrough?.includes(base)) {
      return true;
    }

    if (currentRules.wrapperTypes?.includes(base) && parts.length >= 3) {
      const innerType = parts[1];
      // Inner type is a 2-segment garment → only need 3 parts total (WRAPPER-TYPE-SEGMENT)
      if (innerType && currentRules.firstTwoSegments?.includes(innerType)) {
        return true;
      }
      // Standard inner type → need 4 parts (WRAPPER-TYPE-SIZE-COLOR)
      if (parts.length >= 4) {
        return true;
      }
      return false;
    }

    if (currentRules.doubleSidedPrefixes?.includes(base) && parts.length >= 3) {
      return true;
    }

    if (currentRules.standardGarments?.includes(base) && parts.length >= 3) {
      return true;
    }

    if (currentRules.firstTwoSegments?.includes(base) && parts.length >= 2) {
      return true;
    }

    return false;
  };

  // Compute unused rule prefixes per category (no inventory SKU matches)
  const unusedByCategory = useMemo(() => {
    const result: Record<SKURuleCategoryKey, Set<string>> = {
      standardGarments: new Set(),
      doubleSidedPrefixes: new Set(),
      firstTwoSegments: new Set(),
      wrapperTypes: new Set(),
      passThrough: new Set(),
    };

    if (!inventorySkus.length) return result;

    // Build a map of base segment → SKU part counts for fast lookup
    const baseToPartCounts = new Map<string, number[]>();
    for (const sku of inventorySkus) {
      const parts = sku.split('-');
      const base = parts[0];
      if (!base) continue;
      const existing = baseToPartCounts.get(base);
      if (existing) {
        existing.push(parts.length);
      } else {
        baseToPartCounts.set(base, [parts.length]);
      }
    }

    const hasMatch = (prefix: string, minParts: number) => {
      const counts = baseToPartCounts.get(prefix);
      return counts ? counts.some((c) => c >= minParts) : false;
    };

    for (const prefix of rules.standardGarments ?? []) {
      if (!hasMatch(prefix, 3)) result.standardGarments.add(prefix);
    }
    for (const prefix of rules.doubleSidedPrefixes ?? []) {
      // Double-sided rule strips leading '2' (e.g. 2HT → HT), so check the stripped base
      const strippedBase = prefix.startsWith('2') ? prefix.slice(1) : prefix;
      if (!hasMatch(strippedBase, 3) && !hasMatch(prefix, 3)) {
        result.doubleSidedPrefixes.add(prefix);
      }
    }
    for (const prefix of rules.firstTwoSegments ?? []) {
      if (!hasMatch(prefix, 2)) result.firstTwoSegments.add(prefix);
    }
    // Wrapper types are intentionally NOT checked — the wrapper prefix (e.g. CUST, BLANK)
    // is stripped during normalization, so inventory SKUs never start with the wrapper prefix.
    for (const prefix of rules.passThrough ?? []) {
      if (!hasMatch(prefix, 1)) result.passThrough.add(prefix);
    }

    return result;
  }, [inventorySkus, rules]);

  const removeUnusedInCategory = (category: SKURuleCategoryKey) => {
    const unused = unusedByCategory[category];
    if (!unused.size) return;
    setRules((prev) => ({
      ...prev,
      [category]: (prev[category] || []).filter((g) => !unused.has(g)),
    }));
  };

  // Build a map of uppercase SKU → product for enriching missing entries
  const productByUpperSku = useMemo(() => {
    const map = new Map<string, (typeof products)[number]>();
    for (const p of products) {
      if (p.sku) map.set(p.sku.trim().toUpperCase(), p);
    }
    return map;
  }, [products]);

  // Check for missing SKUs (actual SKU strings)
  useEffect(() => {
    if (!inventorySkus.length) return;

    const missingMap = new Map<string, MissingSkuEntry>();

    inventorySkus.forEach((sku: string) => {
      if (!isSkuCoveredByRules(sku, rules)) {
        const base = sku.split('-')[0] ?? sku;
        if (!missingMap.has(sku)) {
          const product = productByUpperSku.get(sku);
          missingMap.set(sku, {
            sku,
            base,
            title: product?.title,
            stockLevel: product?.stockLevel,
            imageUrl: product?.image_url,
          });
        }
      }
    });

    const sorted = Array.from(missingMap.values()).sort((a, b) => a.sku.localeCompare(b.sku));
    setMissingSkus(sorted);
  }, [inventorySkus, rules, productByUpperSku]);

  const addGarment = (category: SKURuleCategoryKey) => {
    const newGarment = newGarmentInputs[category].trim().toUpperCase();
    if (!newGarment) return;

    // Validation: double-sided prefixes must start with '2'
    if (category === 'doubleSidedPrefixes' && !newGarment.startsWith('2')) {
      return;
    }

    appendRuleValue(category, newGarment);

    setNewGarmentInputs(prev => ({
      ...prev,
      [category]: ''
    }));
  };

  const addMissingSkuToCategory = (entry: MissingSkuEntry, category: SKURuleCategoryKey) => {
    const parts = entry.sku.split('-');
    if (parts.length === 0) return;

    let value = parts[0];
    if (!value) return;

    if (category === 'doubleSidedPrefixes') {
      value = value.startsWith('2') ? value : `2${value}`;
    }

    appendRuleValue(category, value);
  };

  const removeGarment = (category: keyof SKURulesState, garment: string) => {
    setRules(prev => ({
      ...prev,
      [category]: Array.isArray(prev[category])
        ? (prev[category]).filter(g => g !== garment)
        : prev[category],
    }));
  };

  const handleSaveChanges = async () => {
    try {
      await saveRules(rules);
      setOriginalRules({ ...rules });
    } catch (_error) {
      // Error handled by hook
    }
  };

  const resetChanges = () => {
    setRules({ ...originalRules });
    setNewGarmentInputs({
      doubleSidedPrefixes: '',
      standardGarments: '',
      firstTwoSegments: '',
      wrapperTypes: '',
      passThrough: ''
    });
  };

  if (rulesLoading) {
    return (
      <div className="flex flex-col">
        <PageLoader pool="skuRules" />
      </div>
    );
  }

  return (
    <RoleGuard requireAdmin>
      <div className="flex flex-col">
        <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
          <PageActionBar
            ariaLabel="SKU rules actions"
            left={
              hasChanges ? (
                <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-amber-300/80">
                  Unsaved changes
                </span>
              ) : null
            }
            right={
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs gap-1.5"
                  onClick={resetChanges}
                  disabled={!hasChanges || isSaving}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset
                </Button>
                <Button
                  size="sm"
                  className="h-8 px-3 text-xs gap-1.5"
                  onClick={handleSaveChanges}
                  disabled={!hasChanges || isSaving}
                >
                  {isSaving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5" />
                  )}
                  {isSaving ? t('costOfGoods.saving') : t('rules.saveChanges')}
                </Button>
              </>
            }
          />

          {/* Database Info Banner */}
          <Alert className="mb-6 mt-6 border-blue-200 bg-blue-50">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <div className="text-sm">
                <strong>Note:</strong> Rules are stored in the Supabase database. Changes will apply to both frontend and backend systems.
              </div>
            </AlertDescription>
          </Alert>

          {/* PAP Auto-Match Info */}
          <Alert className="mb-6 border-emerald-200 bg-emerald-50">
            <Info className="h-4 w-4 text-emerald-600" />
            <AlertDescription className="text-emerald-800">
              <div className="text-sm">
                <strong>PAP products are auto-matched.</strong> SKUs from pick-and-pack products (synced from Shopify) are matched by exact SKU lookup during order sync and bypass these cleaning rules entirely. Only POD (print-on-demand) SKUs need rules below.
              </div>
            </AlertDescription>
          </Alert>

          {/* Missing Garments Warning Banner */}
          <MissingGarmentsAlert
            missingSkus={missingSkus}
            onAssign={addMissingSkuToCategory}
          />

          <SkuRulesEditor
            rules={rules}
            newGarmentInputs={newGarmentInputs}
            setNewGarmentInputs={setNewGarmentInputs}
            addGarment={addGarment}
            removeGarment={removeGarment}
            isSaving={isSaving}
            appendRuleValue={appendRuleValue}
            inventorySkus={inventorySkus}
            unusedByCategory={unusedByCategory}
            removeUnusedInCategory={removeUnusedInCategory}
          />
      </main>
    </div>
    </RoleGuard>
  );
};

export default SKURules; 
